import { Select } from 'antd5';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClearIcon, SuffixIcon } from 'components/SelectChooser/SelectChooser.style';
import { useMessage } from 'components/ui';
import { useGetSitesMinimalQuery } from 'types/atlas-graphql';

import { useAssetChooserState } from '../AssetChooser/useAssetChooserState';
import { filterOption } from '../helpers';

interface ILocationChooser {
  value: string | string[] | undefined;
  onChange: (locationIds: string[]) => void;
  onClear?: () => void;
  disabled: boolean;
  mode?: 'multiple' | 'tags';
  placeholder?: string;
}

export const LocationChooser: React.FunctionComponent<ILocationChooser> = ({
  value,
  onChange,
  onClear,
  disabled,
  mode,
  placeholder,
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const { handleChange, setVisible, chooserState } = useAssetChooserState({ onChange, value });
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');
  const { data, loading, error } = useGetSitesMinimalQuery();

  useEffect(() => {
    if (error) {
      message.error(error.message, 5);
    }
  }, [error]);

  const locations = sortBy(data?.sites?.items, ['name']) ?? [];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    setVisible(open);
  };

  return (
    <Select
      id="location-chooser-dropdown"
      placeholder={placeholder ?? t('table.selectSite', 'Select Site...')}
      loading={loading}
      allowClear={true}
      showSearch={true}
      filterOption={filterOption}
      virtual={false}
      value={loading ? undefined : chooserState}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      onClear={onClear}
      clearIcon={<ClearIcon />}
      suffixIcon={chooserState ? null : <SuffixIcon />}
      disabled={disabled}
      mode={mode}
      autoClearSearchValue={false}
      searchValue={searchValue}
      onSearch={setSearchValue}
    >
      {locations.map(location => (
        <Select.Option key={location.id} className="location-chooser-option" value={location.id}>
          {location.name}
        </Select.Option>
      ))}
    </Select>
  );
};
